import React, { useState } from 'react'
import { COLORS } from '../theme'
import { TH, TD, SortHeader } from './tableStyles.jsx'

// Epics by developer.
//
// This is the only panel on the board that names individuals, so it is built to
// resist the reading it will otherwise invite. Three choices carry that:
//
//  1. POINTS ARE THE DEFAULT SORT, not epic count. The size ladder runs XS ≤ 10
//     points to XL > 100, so ranking by count puts whoever took the most small
//     work at the top. Sorting by weight is the honest default.
//  2. THE SIZE MIX IS ALWAYS VISIBLE beside the count. A bare "8" hides an order
//     of magnitude; the stacked bar makes two people with the same count and
//     very different work look different, because they are.
//  3. NO RANK NUMBERS, NO TOTALS ROW ACROSS PEOPLE. Position in a sorted list is
//     already a soft ranking; numbering it would assert a league table this data
//     does not support.

const BAND_ORDER = ['XS', 'S', 'M', 'L', 'XL']
const BAND_COLOR = { XS: '#93c5fd', S: '#60a5fa', M: '#3b82f6', L: '#2563eb', XL: '#1d4ed8' }

// Built vs delegated vs unknown, by points.
//
// This is the column that stops a lead's team delivery reading as their personal
// output. It is derived per-epic from the epic's own child issues, NOT from a
// roster: labelling people would hardcode a judgement about individuals that
// goes stale the moment someone changes role, and it would describe the person
// rather than the work.
//
// UNKNOWN is drawn, not hidden. 16 of 20 sampled lead-owned epics had no child
// issues, so for some rows it is most of the bar — and a reader who cannot see
// that would take the visible sliver as the whole story.
const SPLIT = [
    { key: 'selfPoints', label: 'built', color: COLORS.success },
    { key: 'delegatedPoints', label: 'delegated', color: COLORS.accent },
    { key: 'unknownPoints', label: 'unknown', color: COLORS.border },
]

function SplitBar({ row }) {
    const total = SPLIT.reduce((n, s) => n + (row[s.key] || 0), 0)
    if (!total) return <span style={{ fontSize: 11, color: COLORS.textMuted }}>—</span>
    const pct = (v) => Math.round((v / total) * 100)
    return (
        <div
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            title={SPLIT.map((s) => `${s.label}: ${row[s.key] || 0} pts (${pct(row[s.key] || 0)}%)`).join(' · ')}
        >
            <span style={{ display: 'flex', height: 9, width: '100%', borderRadius: 2, overflow: 'hidden', minWidth: 40 }}>
                {SPLIT.filter((s) => row[s.key] > 0).map((s) => (
                    <span key={s.key} style={{ flex: row[s.key], background: s.color }} />
                ))}
            </span>
            <span style={{ fontSize: 10.5, color: COLORS.textMuted, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                {pct(row.selfPoints || 0)}%
            </span>
        </div>
    )
}

export default function DevTable({ rows, note }) {
    const [sort, setSort] = useState('points')
    if (!rows || rows.length === 0) return null

    const sorted = [...rows].sort((a, b) =>
        sort === 'points' ? b.points - a.points || b.total - a.total : b.total - a.total || b.points - a.points
    )
    const maxPoints = Math.max(...sorted.map((r) => r.points || 0), 1)

    // Cell styling and the sort header are SHARED with the board's other table
    // (tableStyles.jsx). They were local consts duplicated per component, which
    // is how the two tables drifted apart while a comment claimed they matched.
    const th = TH
    const td = TD
    const sortBtn = (key, label) => (
        <SortHeader
            label={label}
            active={sort === key}
            onClick={() => setSort(key)}
            title={key === 'points' ? 'Weight of work — the honest default' : 'Raw epic count — ignores size'}
        />
    )

    return (
        <div style={{ marginTop: 10 }}>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 460 }}>
                    <thead>
                        <tr>
                            <th style={th}>Developer</th>
                            <th style={{ ...th, textAlign: 'right' }}>{sortBtn('points', 'Points')}</th>
                            <th style={{ ...th, textAlign: 'right' }}>{sortBtn('total', 'Epics')}</th>
                            <th style={{ ...th, width: '26%' }}>Size mix</th>
                            <th style={{ ...th, width: '26%' }} title="Built = every child issue was the owner's. Delegated = at least one belonged to someone else. Unknown = the epic has no child issues, so it says nothing either way.">
                                Built / delegated
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((r) => (
                            <tr key={r.developer}>
                                <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.developer}</td>
                                <td
                                    style={{
                                        ...td,
                                        textAlign: 'right',
                                        fontVariantNumeric: 'tabular-nums',
                                        fontWeight: 600,
                                    }}
                                >
                                    {r.points}
                                </td>
                                <td
                                    style={{
                                        ...td,
                                        textAlign: 'right',
                                        fontVariantNumeric: 'tabular-nums',
                                        color: COLORS.textMuted,
                                    }}
                                >
                                    {r.total}
                                </td>
                                <td style={td}>
                                    <div
                                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                                        title={BAND_ORDER.filter((b) => r[b] > 0).map((b) => `${b}: ${r[b]}`).join(' · ')}
                                    >
                                        <span
                                            style={{
                                                display: 'flex',
                                                height: 9,
                                                width: `${Math.max((r.points / maxPoints) * 100, 3)}%`,
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                minWidth: 8,
                                            }}
                                        >
                                            {BAND_ORDER.filter((b) => r[b] > 0).map((b) => (
                                                <span
                                                    key={b}
                                                    style={{ flex: r[b], background: BAND_COLOR[b] }}
                                                />
                                            ))}
                                        </span>
                                        <span style={{ fontSize: 10.5, color: COLORS.textMuted, whiteSpace: 'nowrap' }}>
                                            {BAND_ORDER.filter((b) => r[b] > 0).map((b) => `${r[b]}${b}`).join(' ')}
                                        </span>
                                    </div>
                                </td>
                                <td style={td}>
                                    <SplitBar row={r} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                {SPLIT.map((s) => (
                    <span key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ width: 9, height: 9, background: s.color, borderRadius: 2 }} />
                        <span style={{ fontSize: 11, color: COLORS.textMuted }}>{s.label}</span>
                    </span>
                ))}
                <span style={{ fontSize: 11, color: COLORS.textMuted }}>· % is the share BUILT by the owner</span>
            </div>

            {note && (
                <p
                    style={{
                        fontSize: 11.5,
                        color: COLORS.textMuted,
                        lineHeight: 1.55,
                        margin: '10px 0 0',
                        paddingTop: 8,
                        borderTop: `1px dashed ${COLORS.border}`,
                    }}
                >
                    {note}
                </p>
            )}
        </div>
    )
}
